export default class Bubbles {
  constructor(canvasId = "jellyCanvas", options = {}) {
    this.canvas = document.getElementById(canvasId);
    this.ctx = this.canvas.getContext("2d");
    this.mouse = { x: -1000, y: -1000 };
    this.jellies = [];

    this.area = document.querySelector(".js-bubble-area");
    const rect = this.area.getBoundingClientRect();
    this.areaTop = rect.top + window.scrollY;
    this.areaHeight = rect.height;

    this.options = Object.assign({
      initialCount: 5,
      maxCount: 15,
      spawnInterval: 2500
    }, options);

    this.resizeCanvas();
    window.addEventListener("resize", () => this.resizeCanvas());
    this.setupMouseEvents();
    this.spawnInitialBubbles();
    this.startSpawning();
    this.animate();
  }

  resizeCanvas() {
    const rect = this.area.getBoundingClientRect();
    this.areaTop = rect.top + window.scrollY;
    this.areaHeight = rect.height;

    this.canvas.width = rect.width;
    this.canvas.height = rect.height;
    this.canvas.style.width = rect.width + "px";
    this.canvas.style.height = rect.height + "px";
    this.canvas.style.position = "absolute";
    this.canvas.style.top = 0;
    this.canvas.style.left = 0;
  }

  setupMouseEvents() {
    window.addEventListener("mousemove", (e) => {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
    });
    window.addEventListener("mouseleave", () => {
      this.mouse.x = -1000;
      this.mouse.y = -1000;
    });
  }

  spawnInitialBubbles() {
    for (let i = 0; i < this.options.initialCount; i++) {
      this.spawnJelly(true);
    }
  }

  startSpawning() {
    setInterval(() => {
      if (this.jellies.length < this.options.maxCount) {
        this.spawnJelly(false);
      }
    }, this.options.spawnInterval);
  }

  spawnJelly(isInitial) {
    const x = Math.random() * this.canvas.width;
    const y = isInitial
      ? this.canvas.height + Math.random() * this.canvas.height
      : this.canvas.height + Math.random() * 50;
    const radius = Math.random() * (54 - 12) + 12;
    this.jellies.push(new Jelly(x, y, radius));
  }

  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.jellies = this.jellies.filter(j => !j.markedForRemoval);
    for (const jelly of this.jellies) {
      jelly.update(this.mouse, this.areaTop, this.areaHeight);
      jelly.draw(this.ctx);
    }
    requestAnimationFrame(() => this.animate());
  }
}

class Jelly {
  constructor(x, y, baseRadius = 60, pointCount = 32) {
    this.xBase = x;
    this.y = y;
    this.baseRadius = baseRadius;
    this.pointCount = pointCount;
    this.stiffness = 0.05;
    this.damping = 0.8;
    this.velocityY = -(Math.random() * 1.2 + 0.3);

    this.wavePhase = Math.random() * Math.PI * 2;
    this.waveSpeed = 0.02 + Math.random() * 0.02;
    this.waveAmplitude = Math.random() * 10 + 5;

    this.opacity = 0;
    this.life = 0;

    this.avoidanceX = 0;
    this.avoidanceY = 0;
    this.avoidanceDecay = 0.9;

    this.isEscaping = false;
    this.markedForRemoval = false;

    this.points = [];
    for (let i = 0; i < pointCount; i++) {
      const angle = (Math.PI * 2 * i) / pointCount;
      this.points.push({
        angle,
        radius: baseRadius,
        currentRadius: baseRadius,
        velocity: 0
      });
    }
  }

  update(mouse, areaTop, areaHeight) {
    this.wavePhase += this.waveSpeed;
    this.xOffset = Math.sin(this.wavePhase) * this.waveAmplitude;
    this.life += 1;
    this.opacity = Math.min(1, this.life / 60);

    const dx = (this.xBase + this.xOffset) - mouse.x;
    const dy = this.y - mouse.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (!this.isEscaping && dist < 100) {
      this.isEscaping = true;
      const force = (1 - dist / 100) * 2;
      const angle = Math.atan2(dy, dx);
      this.avoidanceX += Math.cos(angle) * force;
      this.avoidanceY += Math.sin(angle) * force;
      this.velocityY = -2.5;
    }

    if (this.isEscaping) {
      this.avoidanceX *= this.avoidanceDecay;
      this.avoidanceY *= this.avoidanceDecay;
      this.xBase += this.avoidanceX;
      this.y += this.avoidanceY + this.velocityY;
      this.baseRadius *= 0.99;
      this.points.forEach(p => p.currentRadius *= 0.995);

      if (this.baseRadius < 1 || this.y + this.baseRadius < 0) {
        this.markedForRemoval = true;
        return;
      }
    } else {
      this.y += this.velocityY;

      const globalY = this.y + areaTop;
      if (globalY < areaTop + 200) {
        this.baseRadius *= 0.98;
        this.points.forEach(p => p.currentRadius *= 0.98);
        if (this.baseRadius < 1) {
          this.markedForRemoval = true;
          return;
        }
      }
    }

    for (let p of this.points) {
      const px = this.xBase + this.xOffset + Math.cos(p.angle) * p.currentRadius;
      const py = this.y + Math.sin(p.angle) * p.currentRadius;
      const dx = px - mouse.x;
      const dy = py - mouse.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const influence = Math.max(0, 1 - dist / 100);
      const target = this.baseRadius - influence * 40;

      const force = (target - p.currentRadius) * this.stiffness;
      p.velocity += force;
      p.velocity *= this.damping;
      p.currentRadius += p.velocity;
    }
  }

  draw(ctx) {
    const x = this.xBase + this.xOffset;
    const o = this.opacity;

    const gradient = ctx.createRadialGradient(
      x, this.y, this.baseRadius * 0.1,
      x, this.y, this.baseRadius
    );
    gradient.addColorStop(0, `rgba(255,255,255,0)`);
    gradient.addColorStop(0.48, `rgba(255,255,255,${0.48 * o})`);
    gradient.addColorStop(0.98, `rgba(148,221,235,${0.98 * o})`);
    gradient.addColorStop(1, `rgba(148,221,235,${1 * o})`);
    ctx.fillStyle = gradient;

    ctx.beginPath();
    for (let i = 0; i <= this.pointCount; i++) {
      const p = this.points[i % this.pointCount];
      const px = x + Math.cos(p.angle) * p.currentRadius;
      const py = this.y + Math.sin(p.angle) * p.currentRadius;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  }
}
